import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/authStore'
import { useTranslation } from 'react-i18next'
import { getLocalizedCategoryName } from '@/lib/localization-helpers'
import { Main } from '@/components/layout/main'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { VariableAutocomplete } from '@/components/variable-autocomplete'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2 } from 'lucide-react'
import {
  FileText,
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileUp,
  Plus,
  Settings,
  ChevronDown,
  ChevronRight,
  Code,

} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import api_base from '@/api/base'
import { useToast } from '@/hooks/use-toast'
import { get_page_layouts, validate_word_template, create_document_template, list_template_variables, create_template_variable } from '@/api/document-templates'
import type { PageLayout, TemplateVariable } from '@/types/document-template'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

export const Route = createFileRoute('/_authenticated/super-admin-document-template-create')({
  beforeLoad: async () => {
    const auth = useAuthStore.getState().auth;

    if (!auth.accessToken || !auth.user) {
      throw redirect({
        to: '/auth/login',
      });
    }

    if (auth.user.role !== 'Super Admin' && auth.user.role_name !== 'Super Admin') {
      throw redirect({
        to: '/dashboard',
      });
    }
  },
  component: Page,
})

function Page() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
  })

  const [engine, setEngine] = useState<'html' | 'word'>('html')
  const [htmlBody, setHtmlBody] = useState<string>('')
  const [categories, setCategories] = useState<Array<{ id: string; name: string; name_ar?: string }>>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [pageLayouts, setPageLayouts] = useState<PageLayout[]>([])
  const [pageLayoutId, setPageLayoutId] = useState<string>('')
  const quillRef = useRef<Quill | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)

  // Word import state
  const [wordFile, setWordFile] = useState<File | null>(null)
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<{
    valid: boolean
    variables: string[]
    valid_variables?: string[]
    invalid_variables?: string[]
    message?: string
    errors?: string[]
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Variable autocomplete state
  const [autocompletePosition, setAutocompletePosition] = useState<{ top: number; left: number } | null>(null)
  const [autocompleteSearch, setAutocompleteSearch] = useState('')
  const autocompleteIndexRef = useRef<number | null>(null)

  // Variables management state
  const [showVariablesSection, setShowVariablesSection] = useState(false)
  const [availableVariables, setAvailableVariables] = useState<TemplateVariable[]>([])
  const [loadingVariables, setLoadingVariables] = useState(false)
  const [showCreateVariableDialog, setShowCreateVariableDialog] = useState(false)
  const [newVariableData, setNewVariableData] = useState({ key: '', label: '', label_ar: '' })
  const [creatingVariable, setCreatingVariable] = useState(false)


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Check file type
    if (!file.name.endsWith('.docx')) {
      toast({
        title: t('error') || 'Error',
        description: t('invalid_file_type') || 'Please select a .docx file',
        variant: 'destructive',
      })
      return
    }

    setWordFile(file)
    setValidationResult(null)

    // Auto-validate the file
    setValidating(true)
    try {
      const result = await validate_word_template(file)
      setValidationResult(result)

      if (result.valid) {
        toast({
          title: t('validation_success') || 'Validation Successful',
          description: `${t('found_variables') || 'Found'} ${result.variables.length} ${t('variables') || 'variables'}`,
        })
      } else {
        toast({
          title: t('validation_failed') || 'Validation Failed',
          description: `${result.invalid_variables?.length || 0} ${t('invalid_variables') || 'invalid variables'} found`,
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      console.error('Validation error:', error)

      // Try to extract validation data from error response (400 returns data)
      const errorData = error?.response?.data
      if (errorData && typeof errorData === 'object' && 'valid' in errorData) {
        setValidationResult(errorData)
        toast({
          title: t('validation_failed') || 'Validation Failed',
          description: errorData.errors?.[0] || t('invalid_variables_found') || 'Invalid variables found',
          variant: 'destructive',
        })
      } else {
        toast({
          title: t('validation_error') || 'Validation Error',
          description: error?.response?.data?.message || error?.message || t('failed_to_validate') || 'Failed to validate document',
          variant: 'destructive',
        })
      }
    } finally {
      setValidating(false)
    }
  }

  const handleVariableSelect = (variableKey: string) => {
    const quill = quillRef.current
    if (quill && autocompleteIndexRef.current !== null) {
      // Get current selection/cursor position
      const currentSelection = quill.getSelection()
      const currentIndex = currentSelection?.index || autocompleteIndexRef.current

      // Calculate the length to delete (everything typed after {{, including any search text)
      const deleteLength = currentIndex - autocompleteIndexRef.current

      // Delete the {{ and any text typed after it (the search text)
      if (deleteLength > 0) {
        quill.deleteText(autocompleteIndexRef.current, deleteLength)
      }

      // Build the final variable text - clean format
      const variableText = `{{${variableKey}}}`

      // Insert the variable at the correct position
      quill.insertText(autocompleteIndexRef.current, variableText)

      // Add a space after the variable for better readability if not already present
      const textAfter = quill.getText(autocompleteIndexRef.current + variableText.length, 1)
      if (textAfter && textAfter !== ' ' && textAfter !== '\n') {
        quill.insertText(autocompleteIndexRef.current + variableText.length, ' ')
      }

      // Move cursor right after the inserted variable (and the optional space)
      const finalCursorPos = autocompleteIndexRef.current + variableText.length + (textAfter && textAfter !== ' ' && textAfter !== '\n' ? 1 : 0)
      quill.setSelection(finalCursorPos)

      // Focus the editor to ensure cursor is visible
      quill.focus()
    }

    // Close autocomplete
    setAutocompletePosition(null)
    autocompleteIndexRef.current = null
    setAutocompleteSearch('')
  }

  useEffect(() => {
    if (editorContainerRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean'],
          ],
        },
      })

      // RTL by default for Arabic; can be toggled via alignment
      editorContainerRef.current.setAttribute('dir', 'rtl')

      quillRef.current.on('text-change', (_delta, _oldDelta, source) => {
        const html = quillRef.current?.root.innerHTML || ''
        setHtmlBody(html)

        console.log('[CreateTemplate] text-change event, source:', source)

        // Detect {{ typing for variable autocomplete
        if (source === 'user') {
          const selection = quillRef.current?.getSelection()
          console.log('[CreateTemplate] selection:', selection)
          if (selection) {
            const text = quillRef.current?.getText(0, selection.index) || ''
            const lastTwoChars = text.slice(-2)
            console.log('[CreateTemplate] text up to cursor:', JSON.stringify(text), 'lastTwoChars:', JSON.stringify(lastTwoChars))

            // Check if we just typed {{ (and autocomplete is not already open)
            if (lastTwoChars === '{{' && !autocompletePosition) {
              // Show autocomplete - new {{ detected
              console.log('[CreateTemplate] Found {{, showing autocomplete')
              const bounds = quillRef.current?.getBounds(selection.index)
              console.log('[CreateTemplate] bounds:', bounds)
              if (bounds && editorContainerRef.current) {
                const editorRect = editorContainerRef.current.getBoundingClientRect()
                console.log('[CreateTemplate] editorRect:', editorRect)
                const position = {
                  top: editorRect.top + bounds.top + bounds.height,
                  left: editorRect.left + bounds.left,
                }
                console.log('[CreateTemplate] Setting position:', position)
                setAutocompletePosition(position)
                setAutocompleteSearch('')
                autocompleteIndexRef.current = selection.index - 2 // Store position of {{
                console.log('[CreateTemplate] autocompleteIndexRef:', autocompleteIndexRef.current)
              }
            }
            // Check if we're inside an autocomplete (between {{ and cursor, no closing }})
            else if (autocompleteIndexRef.current !== null) {
              const currentText = quillRef.current?.getText() || ''

              // Find the most recent {{ before current cursor
              const beforeCursor = currentText.substring(0, selection.index)
              const lastOpenBraceIndex = beforeCursor.lastIndexOf('{{')

              console.log('[CreateTemplate] Checking if still in autocomplete... lastOpenBraceIndex:', lastOpenBraceIndex, 'autocompleteIndexRef:', autocompleteIndexRef.current)

              // Check if {{ is found and is after or at our stored index
              if (lastOpenBraceIndex >= autocompleteIndexRef.current - 1) {
                const searchStart = lastOpenBraceIndex + 2 // Skip {{
                const searchEnd = selection.index
                const searchText = currentText.slice(searchStart, searchEnd)

                console.log('[CreateTemplate] In autocomplete - extracted search text:', JSON.stringify(searchText), 'from', searchStart, 'to', searchEnd)

                // Close autocomplete if user types closing }}
                if (searchText.includes('}}')) {
                  console.log('[CreateTemplate] Closing autocomplete - found }}')
                  setAutocompletePosition(null)
                  autocompleteIndexRef.current = null
                  setAutocompleteSearch('')
                } else {
                  // Update search text for server-side filtering
                  console.log('[CreateTemplate] Setting search text:', JSON.stringify(searchText))
                  setAutocompleteSearch(searchText)

                  // Keep position active
                  if (!autocompletePosition) {
                    const bounds = quillRef.current?.getBounds(selection.index)
                    if (bounds && editorContainerRef.current) {
                      const editorRect = editorContainerRef.current.getBoundingClientRect()
                      setAutocompletePosition({
                        top: editorRect.top + bounds.top + bounds.height,
                        left: editorRect.left + bounds.left,
                      })
                    }
                  }
                }
              } else {
                // {{ not found, close autocomplete
                console.log('[CreateTemplate] No {{ found, closing autocomplete')
                setAutocompletePosition(null)
                autocompleteIndexRef.current = null
                setAutocompleteSearch('')
              }
            }
          }
        }
      })
    }
  }, [])

  // Fetch categories
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api_base.get('/api/v1/super-admin/document-template-categories/')
        const data = res.data
        const items = Array.isArray(data) ? data : (data?.results ?? [])
        setCategories(items)
      } catch (error) {
        console.error('Failed to fetch categories', error)
      }
    }
    load()
  }, [])

  // Fetch page layouts
  useEffect(() => {
    const load = async () => {
      try {
        const response = await get_page_layouts()
        console.log('[PageLayouts] Fetched response:', response)
        // Handle paginated response
        const layouts = response.results || response
        setPageLayouts(Array.isArray(layouts) ? layouts : [])
      } catch (error) {
        console.error('Failed to fetch page layouts', error)
      }
    }
    load()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.slug.trim()) {
      toast({
        title: t('error') || 'Error',
        description: t('fill_required_fields') || 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }

    if (engine === 'html' && !htmlBody.trim()) {
      toast({
        title: t('error') || 'Error',
        description: t('template_content_required') || 'Please provide template content',
        variant: 'destructive',
      })
      return
    }

    if (engine === 'word' && !wordFile) {
      toast({
        title: t('error') || 'Error',
        description: t('word_file_required') || 'Please upload a Word document',
        variant: 'destructive',
      })
      return
    }

    if (engine === 'word' && validationResult && !validationResult.valid) {
      toast({
        title: t('error') || 'Error',
        description: t('fix_validation_errors') || 'Please fix validation errors before submitting',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      await create_document_template({
        slug: formData.slug,
        title: formData.title,
        engine: engine,
        html_body: engine === 'html' ? htmlBody : undefined,
        category: selectedCategoryId || null,
        page_layout: engine === 'html' ? (pageLayoutId || null) : null,
        file: engine === 'word' ? wordFile! : undefined,
      })

      toast({
        title: t('success') || 'Success',
        description: t('template_created') || 'Document template created successfully',
      })

      navigate({ to: '/super-admin-document-templates' })
    } catch (error: any) {
      console.error('Error creating template:', error)
      const errorMsg = error?.response?.data?.detail || error?.response?.data?.message || t('error_creating_template') || 'Failed to create template'
      toast({
        title: t('error') || 'Error',
        description: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Load available variables
  const loadVariables = async () => {
    setLoadingVariables(true)
    try {
      const response = await list_template_variables({ page_size: 50 })
      setAvailableVariables(response.results)
    } catch (error) {
      console.error('Failed to load variables:', error)
    } finally {
      setLoadingVariables(false)
    }
  }

  // Handle variable creation from variables section
  const handleCreateVariableFromSection = async () => {
    if (!newVariableData.key || !newVariableData.label) {
      toast({
        title: t('error') || 'Error',
        description: t('fill_required_fields') || 'Please fill required fields',
        variant: 'destructive',
      })
      return
    }

    // Validate key format
    if (!/^[a-z_][a-z0-9_]*(\.[a-z_][a-z0-9_]*)*$/i.test(newVariableData.key)) {
      toast({
        title: t('error') || 'Error',
        description: t('invalid_key_format') || 'Key must be in format: entity.property (e.g., client.name)',
        variant: 'destructive',
      })
      return
    }

    setCreatingVariable(true)
    try {
      await create_template_variable(newVariableData)
      setShowCreateVariableDialog(false)
      setNewVariableData({ key: '', label: '', label_ar: '' })
      loadVariables() // Refresh the list
      toast({
        title: t('success') || 'Success',
        description: t('variable_created_successfully') || 'Variable created successfully',
      })
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.response?.data?.message || error?.message
      toast({
        title: t('error_creating_variable') || 'Failed to create variable',
        description: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg),
        variant: 'destructive',
      })
    } finally {
      setCreatingVariable(false)
    }
  }

  // Insert variable into editor
  const insertVariableIntoEditor = (variableKey: string) => {
    const quill = quillRef.current
    if (quill) {
      const selection = quill.getSelection()
      const index = selection ? selection.index : quill.getLength()

      // Insert variable with proper formatting
      const variableText = `{{${variableKey}}}`
      quill.insertText(index, variableText)

      // Add space if needed
      const textAfter = quill.getText(index + variableText.length, 1)
      if (textAfter && textAfter !== ' ' && textAfter !== '\n') {
        quill.insertText(index + variableText.length, ' ')
      }

      // Move cursor and focus
      const finalPos = index + variableText.length + (textAfter && textAfter !== ' ' && textAfter !== '\n' ? 1 : 0)
      quill.setSelection(finalPos)
      quill.focus()
    }
  }

  // Load variables when showing variables section
  useEffect(() => {
    if (showVariablesSection && availableVariables.length === 0) {
      loadVariables()
    }
  }, [showVariablesSection])

  return (
    <>


      <Main>
        <div className='mb-6'>
          <Button
            variant='ghost'
            size='sm'
            className='mb-4 gap-2'
            onClick={() => navigate({ to: '/super-admin-document-templates' })}
            disabled={isLoading}
          >
            <ArrowLeft className='w-4 h-4' />
            {t('back') || 'Back'}
          </Button>

          <div className='flex items-center gap-3 mb-4'>
            <FileText className='w-8 h-8' />
            <div>
              <h2 className='text-2xl font-bold tracking-tight'>
                {t('create_template') || 'Create Document Template'}
              </h2>
              <p className='text-muted-foreground'>
                {t('create_new_template_desc') || 'Create a new HTML-based document template with schema and variables'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='max-w-6xl space-y-6'>
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('template_details') || 'Template Details'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='title'>
                    {t('title') || 'Title'} <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='title'
                    name='title'
                    value={formData.title}
                    onChange={handleChange}
                    placeholder={t('enter_template_title') || 'نموذج عقد خدمة'}
                    disabled={isLoading}
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='slug'>
                    {t('slug') || 'Slug'} <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='slug'
                    name='slug'
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder='contract-template'
                    disabled={isLoading}
                  />
                  <p className='text-xs text-muted-foreground'>
                    {t('slug_help') || 'Unique identifier (lowercase, hyphens only)'}
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='engine'>
                    {t('template_engine') || 'Template Engine'} <span className='text-destructive'>*</span>
                  </Label>
                  <Select value={engine} onValueChange={(val) => setEngine(val as 'html' | 'word')}>
                    <SelectTrigger id='engine'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='html'>
                        {t('html_editor') || 'HTML Editor'}
                      </SelectItem>
                      <SelectItem value='word'>
                        {t('word_document') || 'Word Document (.docx)'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground'>
                    {engine === 'html'
                      ? (t('html_editor_desc') || 'Create template using visual editor')
                      : (t('word_document_desc') || 'Upload a Word document with {{variables}}')
                    }
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='category'>
                    {t('category') || 'Category'}
                  </Label>
                  <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                    <SelectTrigger id='category'>
                      <SelectValue placeholder={t('select_category') || 'Select a category'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {getLocalizedCategoryName(c, i18n.language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {engine === 'html' && (
                  <div className='space-y-2'>
                    <Label htmlFor='page_layout'>
                      {t('page_layout') || 'Page Layout'}
                    </Label>
                    <Select value={pageLayoutId} onValueChange={setPageLayoutId}>
                      <SelectTrigger id='page_layout'>
                        <SelectValue placeholder={t('select_page_layout') || 'Select a page layout'} />
                      </SelectTrigger>
                      <SelectContent>
                        {pageLayouts.map((layout) => (
                          <SelectItem key={layout.id} value={layout.id}>
                            {layout.name_ar || layout.name}
                            {layout.page_size && ` (${layout.page_size})`}
                            {layout.orientation && ` - ${layout.orientation}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Word Document Import */}
          {engine === 'word' && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <FileUp className='w-5 h-5' />
                  {t('upload_word_document') || 'Upload Word Document'}
                </CardTitle>
                <CardDescription>
                  {t('upload_word_desc') || 'Upload a .docx file with variables in the format {{variable.name}}'}
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-3'>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.docx'
                    onChange={handleFileSelect}
                    className='hidden'
                  />

                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => fileInputRef.current?.click()}
                    disabled={validating || isLoading}
                    className='w-full gap-2'
                  >
                    <Upload className='w-4 h-4' />
                    {wordFile ? (t('change_file') || 'Change File') : (t('select_file') || 'Select File')}
                  </Button>

                  {wordFile && (
                    <div className='p-3 rounded-md bg-muted flex items-center gap-3'>
                      <FileText className='w-5 h-5 text-muted-foreground' />
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium truncate'>{wordFile.name}</p>
                        <p className='text-xs text-muted-foreground'>
                          {(wordFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  )}

                  {validating && (
                    <Alert>
                      <AlertDescription className='flex items-center gap-2'>
                        <div className='w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin' />
                        {t('validating_document') || 'Validating document...'}
                      </AlertDescription>
                    </Alert>
                  )}

                  {validationResult && (
                    <Alert variant={validationResult.valid ? 'default' : 'destructive'}>
                      <AlertDescription>
                        <div className='flex items-start gap-2'>
                          {validationResult.valid ? (
                            <CheckCircle2 className='w-5 h-5 mt-0.5 flex-shrink-0 text-green-600' />
                          ) : (
                            <AlertCircle className='w-5 h-5 mt-0.5 flex-shrink-0' />
                          )}
                          <div className='flex-1 space-y-3'>
                            <p className='font-medium'>
                              {validationResult.valid
                                ? (t('validation_passed') || 'Validation Passed')
                                : (t('validation_failed') || 'Validation Failed')
                              }
                            </p>

                            {validationResult.errors && validationResult.errors.length > 0 && (
                              <div className='space-y-1'>
                                <p className='text-sm font-medium text-destructive'>
                                  {t('errors') || 'Errors'}:
                                </p>
                                <div className='space-y-1'>
                                  {validationResult.errors.map((error, i) => (
                                    <div key={i} className='text-sm text-destructive/90 bg-destructive/5 p-2 rounded border border-destructive/20'>
                                      {error}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {validationResult.valid_variables && validationResult.valid_variables.length > 0 && (
                              <div className='space-y-1'>
                                <p className='text-sm font-medium text-green-700'>
                                  {t('valid_variables') || 'Valid Variables'} ({validationResult.valid_variables.length}):
                                </p>
                                <div className='flex flex-wrap gap-1'>
                                  {validationResult.valid_variables.map((v, i) => (
                                    <Badge key={i} variant='secondary' className='text-xs bg-green-100 text-green-800'>
                                      ✓ {v}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {validationResult.invalid_variables && validationResult.invalid_variables.length > 0 && (
                              <div className='space-y-1'>
                                <p className='text-sm font-medium text-destructive'>
                                  {t('invalid_variables') || 'Invalid Variables'} ({validationResult.invalid_variables.length}):
                                </p>
                                <div className='flex flex-wrap gap-1'>
                                  {validationResult.invalid_variables.map((v, i) => (
                                    <Badge key={i} variant='destructive' className='text-xs'>
                                      ✗ {v}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* WYSIWYG Editor - Only for HTML engine */}
          {engine === 'html' && (
            <Card>
              <CardHeader>
                <CardTitle className='text-base'>{t('template_content') || 'Template Content'}</CardTitle>
                <CardDescription>
                  {t('template_content_desc') || 'Use the rich text editor to compose your document. Insert variables to personalize content.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='mb-3 p-3 rounded-md bg-muted/50 text-sm'>
                  <p className='font-medium mb-1'>{t('tip') || 'Tip'}:</p>
                  <p className='text-muted-foreground'>
                    {t('variable_autocomplete_help') || 'Type {{ in the editor to open variable autocomplete. Select an existing variable or create a new one.'}
                  </p>
                </div>

                <div
                  ref={editorContainerRef}
                  className='min-h-[300px] rounded-md border border-input bg-background'
                />
              </CardContent>
            </Card>
          )}

          {/* Variables Management Section */}
          {engine === 'html' && (
            <Card>
              <CardHeader>
                <Collapsible open={showVariablesSection} onOpenChange={setShowVariablesSection}>
                  <CollapsibleTrigger asChild>
                    <div className='flex items-center justify-between w-full cursor-pointer'>
                      <div className='flex items-center gap-2'>
                        <Code className='w-5 h-5' />
                        <CardTitle className='text-base'>{t('template_variables') || 'Template Variables'}</CardTitle>
                      </div>
                      {showVariablesSection ? (
                        <ChevronDown className='w-4 h-4' />
                      ) : (
                        <ChevronRight className='w-4 h-4' />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className='space-y-4'>
                    <CardDescription>
                      {t('variables_management_desc') || 'Manage template variables that can be inserted into your document.'}
                    </CardDescription>

                    <div className='flex justify-between items-center'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setShowCreateVariableDialog(true)}
                        className='gap-2'
                      >
                        <Plus className='w-4 h-4' />
                        {t('create_variable') || 'Create Variable'}
                      </Button>

                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={loadVariables}
                        disabled={loadingVariables}
                        className='gap-2'
                      >
                        <Settings className='w-4 h-4' />
                        {loadingVariables ? t('loading') || 'Loading...' : t('refresh') || 'Refresh'}
                      </Button>
                    </div>

                    {loadingVariables && (
                      <div className='flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground'>
                        <Loader2 className='w-4 h-4 animate-spin' />
                        {t('loading_variables') || 'Loading variables...'}
                      </div>
                    )}

                    {!loadingVariables && availableVariables.length === 0 && (
                      <div className='p-4 text-center text-muted-foreground'>
                        <p>{t('no_variables_found') || 'No variables found'}</p>
                        <p className='text-xs'>{t('create_first_variable') || 'Create your first variable to get started'}</p>
                      </div>
                    )}

                    {!loadingVariables && availableVariables.length > 0 && (
                      <div className='space-y-2 max-h-60 overflow-y-auto'>
                        {availableVariables.map((variable) => (
                          <div
                            key={variable.id}
                            className='flex items-center justify-between p-3 rounded-md border hover:bg-muted/50 transition-colors'
                          >
                            <div className='flex-1 min-w-0'>
                              <div className='flex items-center gap-2 mb-1'>
                                <code className='text-xs font-mono bg-muted px-2 py-0.5 rounded'>
                                  {variable.key}
                                </code>
                                <Badge
                                  variant={variable.source === 'system' ? 'default' : 'secondary'}
                                  className='text-xs'
                                >
                                  {variable.source === 'system'
                                    ? t('system_variable') || 'System'
                                    : t('custom_variable') || 'Custom'}
                                </Badge>
                              </div>
                              <p className='text-sm font-medium text-foreground truncate'>
                                {variable.label_ar || variable.label}
                              </p>
                              {variable.description && (
                                <p className='text-xs text-muted-foreground truncate'>
                                  {variable.description_ar || variable.description}
                                </p>
                              )}
                            </div>
                            <Button
                              type='button'
                              variant='outline'
                              size='sm'
                              onClick={() => insertVariableIntoEditor(variable.key)}
                              className='gap-1 ml-3'
                            >
                              <Plus className='w-3 h-3' />
                              {t('insert') || 'Insert'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </CardHeader>
            </Card>
          )}

          {/* Submit Button moved to bottom */}
          <div className='flex justify-end gap-3'>
            <Button
              type='button'
              variant='outline'
              onClick={() => navigate({ to: '/super-admin-document-templates' })}
              disabled={isLoading}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button type='submit' disabled={isLoading || validating}>
              {isLoading ? (t('creating') || 'Creating...') : (t('create_template') || 'Create Template')}
            </Button>
          </div>
        </form>

        {/* Variable Autocomplete */}
        {engine === 'html' && (
          <VariableAutocomplete
            position={autocompletePosition}
            searchText={autocompleteSearch}
            onSelect={handleVariableSelect}
            onClose={() => {
              setAutocompletePosition(null)
              autocompleteIndexRef.current = null
              setAutocompleteSearch('')
            }}
          />
        )}

        {/* Create Variable Dialog from Variables Section */}
        <Dialog open={showCreateVariableDialog} onOpenChange={setShowCreateVariableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('create_new_variable') || 'Create Variable'}</DialogTitle>
            </DialogHeader>

            <div className='space-y-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor='new-key'>
                  {t('variable_key') || 'Key'} <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='new-key'
                  value={newVariableData.key}
                  onChange={(e) => setNewVariableData(prev => ({
                    ...prev,
                    key: e.target.value.toLowerCase().replace(/\s+/g, '_')
                  }))}
                  placeholder='client.name'
                  disabled={creatingVariable}
                  autoFocus
                />
                <p className='text-xs text-muted-foreground'>
                  {t('key_format_help') || 'Format: entity.property (e.g., client.name, case.number)'}
                </p>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-label'>
                  {t('variable_label') || 'Label'} <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='new-label'
                  value={newVariableData.label}
                  onChange={(e) => setNewVariableData(prev => ({
                    ...prev,
                    label: e.target.value
                  }))}
                  placeholder='Client Name'
                  disabled={creatingVariable}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-label-ar'>{t('variable_label_ar') || 'Label (Arabic)'}</Label>
                <Input
                  id='new-label-ar'
                  value={newVariableData.label_ar}
                  onChange={(e) => setNewVariableData(prev => ({
                    ...prev,
                    label_ar: e.target.value
                  }))}
                  placeholder='اسم الموكل'
                  disabled={creatingVariable}
                />
              </div>

              {/* Preview */}
              <div className='p-3 rounded-md bg-muted/50 space-y-2'>
                <p className='text-xs font-medium text-muted-foreground'>{t('preview') || 'Preview'}:</p>
                <div className='flex items-center gap-2'>
                  <code className='text-sm font-mono bg-background px-2 py-1 rounded flex-1'>
                    {`{{${newVariableData.key || '...'}}}`}
                  </code>
                </div>
                {newVariableData.label && (
                  <p className='text-sm text-foreground'>{newVariableData.label}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setShowCreateVariableDialog(false)}
                disabled={creatingVariable}
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleCreateVariableFromSection}
                disabled={creatingVariable || !newVariableData.key || !newVariableData.label}
              >
                {creatingVariable && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
                {creatingVariable ? t('creating_variable') || 'Creating...' : t('create') || 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Main>
    </>
  )
}
